import type { KeyDef, KeyboardLayoutId } from '../../types';
import { FINGER_COLORS, FINGER_NAMES } from './fingers';
import { MAC_DK_LAYOUT } from './mac-dk';
import { PC_DK_LAYOUT } from './pc-dk';

export { FINGER_COLORS, FINGER_NAMES };

const LAYOUTS: Record<KeyboardLayoutId, KeyDef[][]> = {
  'mac-dk': MAC_DK_LAYOUT,
  'pc-dk': PC_DK_LAYOUT,
};

export const KEYBOARD_LAYOUT_LABELS: Record<
  KeyboardLayoutId,
  { label: string; description: string }
> = {
  'mac-dk': { label: 'Mac (dansk)', description: 'Apple-tastatur' },
  'pc-dk': { label: 'PC (dansk)', description: 'Windows / Linux ISO' },
};

export function getKeyboardLayout(id: KeyboardLayoutId): KeyDef[][] {
  return LAYOUTS[id] ?? MAC_DK_LAYOUT;
}

export function keyDefForChar(
  ch: string,
  layoutId: KeyboardLayoutId,
): KeyDef | undefined {
  if (!ch) return undefined;
  const lower = ch.toLowerCase();
  for (const row of getKeyboardLayout(layoutId)) {
    for (const k of row) {
      if (k.decorative) continue;
      if (k.char === lower) return k;
      if (k.shift && k.shift.toLowerCase() === lower) return k;
    }
  }
  return undefined;
}

export function fingerNameForChar(
  ch: string,
  layoutId: KeyboardLayoutId,
): string | null {
  const def = keyDefForChar(ch, layoutId);
  return def ? FINGER_NAMES[def.finger] : null;
}

export interface ResolvedExpectedKey {
  baseChar: string;
  needsShift: boolean;
  def: KeyDef | undefined;
}

/** Map expected character to physical key + whether Shift is required. */
export function resolveExpectedKey(
  ch: string,
  layoutId: KeyboardLayoutId,
): ResolvedExpectedKey {
  if (ch === ' ') {
    return { baseChar: ' ', needsShift: false, def: keyDefForChar(' ', layoutId) };
  }
  const def = keyDefForChar(ch, layoutId);
  if (!def) {
    return {
      baseChar: ch.toLowerCase(),
      needsShift: ch !== ch.toLowerCase(),
      def: undefined,
    };
  }
  if (def.shift === ch) {
    return { baseChar: def.char, needsShift: true, def };
  }
  if (def.char === ch) {
    return { baseChar: def.char, needsShift: false, def };
  }
  if (
    def.char &&
    ch.length === 1 &&
    ch.toUpperCase() === ch &&
    def.char === ch.toLowerCase()
  ) {
    return { baseChar: def.char, needsShift: true, def };
  }
  return { baseChar: ch, needsShift: false, def };
}

export function isCharUnlocked(
  ch: string,
  unlockedKeys: ReadonlyArray<string>,
): boolean {
  const set = new Set(unlockedKeys);
  if (set.has(ch)) return true;
  if (ch === ' ') return set.has(' ');
  const lower = ch.toLowerCase();
  if (set.has(lower)) return true;
  return false;
}
